/**
 * Batch Operations API hooks
 *
 * TanStack Query hooks for batch operation management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

// Types
export type BatchOperationStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type BatchLocationFilter =
  | 'non_enriched'
  | 'enriched'
  | 'non_tagged'
  | 'tagged'
  | 'non_published'
  | 'published'
  | 'pending'
  | 'all';

export interface BatchLocationResult {
  locationId: string;
  locationName: string;
  status: 'success' | 'failed' | 'skipped';
  message?: string;
  details?: Record<string, unknown>;
  processedAt?: string; // ISO timestamp for cursor-based pagination
}

export interface BatchOperation {
  id: string;
  operationType: string;
  status: string;
  partnerAccountId: string;
  partnerAccountName?: string;
  locationFilter: string | null;
  locationIds: string[] | null;
  config: Record<string, unknown> | null;
  totalCount: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  results: BatchLocationResult[] | null;
  startedBy: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BatchOperationPreview {
  operationType: string;
  partnerAccountId: string;
  locationFilter: string | null;
  eligibleCount: number;
  locations?: {
    id: string;
    name: string;
    enrichmentStatus: string;
    publicationStatus: string;
  }[];
}

export interface PaginatedBatchOperations {
  data: BatchOperation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateBatchOperationInput {
  operationType: 'publish' | 'auto_enrich' | 'batch_tags' | 'batch_publish' | 'generate_descriptions' | 'bulk_update' | 'ai_content' | 'batch_classify_type' | 'batch_resync_content';
  partnerAccountId: string;
  locationFilter?: BatchLocationFilter;
  locationIds?: string[];
  config?: Record<string, unknown>;
}

export interface BatchOperationProgress {
  id: string;
  status: BatchOperationStatus;
  totalCount: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  results: BatchLocationResult[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface BatchProgressParams {
  since?: string; // ISO timestamp cursor
  limit?: number; // max results (default 50)
}

// Query keys
export const batchOperationKeys = {
  all: ['batch-operations'] as const,
  list: (params: ListParams) => [...batchOperationKeys.all, 'list', params] as const,
  detail: (id: string) => [...batchOperationKeys.all, 'detail', id] as const,
  preview: (params: PreviewParams) => [...batchOperationKeys.all, 'preview', params] as const,
  progress: (id: string, params?: BatchProgressParams) =>
    [...batchOperationKeys.all, 'progress', id, params] as const,
};

interface ListParams {
  partnerAccountId?: string;
  operationType?: string;
  status?: string;
  page?: number;
  limit?: number;
}

interface PreviewParams {
  operationType: string;
  partnerAccountId: string;
  locationFilter?: string;
}

/**
 * List batch operations with filtering and pagination
 */
export function useBatchOperations(params: ListParams = {}) {
  const queryParams = new URLSearchParams();
  if (params.partnerAccountId) queryParams.set('partnerAccountId', params.partnerAccountId);
  if (params.operationType) queryParams.set('operationType', params.operationType);
  if (params.status) queryParams.set('status', params.status);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());

  const query = queryParams.toString();

  return useQuery({
    queryKey: batchOperationKeys.list(params),
    queryFn: () =>
      api.get<PaginatedBatchOperations>(
        `/api/v1/batch-operations${query ? `?${query}` : ''}`
      ),
    // Poll every 5 seconds for running operations
    refetchInterval: (queryData) => {
      const data = queryData.state.data;
      if (!data) return false;
      const hasRunning = data.data.some((op) => op.status === 'running' || op.status === 'pending');
      return hasRunning ? 5000 : false;
    },
  });
}

/**
 * Get a single batch operation by ID
 */
export function useBatchOperation(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: batchOperationKeys.detail(id),
    queryFn: () => api.get<BatchOperation>(`/api/v1/batch-operations/${id}`),
    enabled: options?.enabled !== false && !!id,
    // Poll every 3 seconds while operation is running
    refetchInterval: (queryData) => {
      const data = queryData.state.data;
      if (!data) return false;
      return data.status === 'running' || data.status === 'pending' ? 3000 : false;
    },
  });
}

/**
 * Preview batch operation (get eligible location count)
 */
export function useGetBatchPreview() {
  return useMutation({
    mutationFn: (input: CreateBatchOperationInput) =>
      api.post<BatchOperationPreview>('/api/v1/batch-operations/preview', input),
  });
}

/**
 * Create a new batch operation
 */
export function useCreateBatchOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateBatchOperationInput) =>
      api.post<BatchOperation>('/api/v1/batch-operations', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.all });
    },
  });
}

/**
 * Pause a running batch operation
 */
export function usePauseBatchOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ success: boolean }>(`/api/v1/batch-operations/${id}/pause`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.all });
    },
  });
}

/**
 * Resume a paused batch operation
 */
export function useResumeBatchOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ success: boolean }>(`/api/v1/batch-operations/${id}/resume`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.all });
    },
  });
}

/**
 * Cancel a batch operation
 */
export function useCancelBatchOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ success: boolean }>(`/api/v1/batch-operations/${id}/cancel`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: batchOperationKeys.all });
    },
  });
}

interface UseBatchOperationProgressOptions {
  enabled?: boolean;
}

/**
 * Get batch operation progress with cursor-based pagination
 *
 * Polls every 3 seconds while the operation is running and hasMore is true.
 * Use the `since` parameter to fetch results processed after a specific timestamp.
 */
export function useBatchOperationProgress(
  id: string | number,
  params?: BatchProgressParams,
  options?: UseBatchOperationProgressOptions
) {
  const stringId = String(id);

  const queryParams = new URLSearchParams();
  if (params?.since) queryParams.set('since', params.since);
  if (params?.limit) queryParams.set('limit', params.limit.toString());

  const query = queryParams.toString();

  return useQuery({
    queryKey: batchOperationKeys.progress(stringId, params),
    queryFn: () =>
      api.get<BatchOperationProgress>(
        `/api/v1/batch-operations/${stringId}/progress${query ? `?${query}` : ''}`
      ),
    enabled: options?.enabled !== false && !!id,
    // Poll every 3 seconds while operation is running and there's more data
    refetchInterval: (queryData) => {
      const data = queryData.state.data;
      if (!data) return false;
      const isRunning = data.status === 'running' || data.status === 'pending';
      return isRunning && data.hasMore ? 3000 : false;
    },
  });
}

